import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { KeyRound, BellRing, ShieldCheck, Globe2 } from 'lucide-react';
import TwoFactorSettings from '@/components/staff/TwoFactorSettings';

function errMsg(e, fallback) {
  const data = e?.response?.data;
  const d = data?.detail;
  return (
    e?.userMessage
    || d?.error?.message || d?.message || (typeof d === 'string' ? d : null)
    || data?.error?.message || data?.message || (typeof data === 'string' ? data : null)
  ) || fallback;
}

function PasswordCard() {
  const [form, setForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const change = async () => {
    if (form.new_password !== form.confirm) { toast.error('New passwords do not match'); return; }
    setBusy(true);
    try {
      const r = await api.post('/auth/change-password', { old_password: form.old_password, new_password: form.new_password });
      if (r.data?.access_token) localStorage.setItem('access_token', r.data.access_token);
      if (r.data?.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
      toast.success('Password changed successfully');
      setForm({ old_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(errMsg(e, 'Unable to change password')); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="settings-password">
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" />Change password</CardTitle></CardHeader>
      <CardContent className="space-y-3 max-w-md">
        <label className="text-xs text-muted-foreground space-y-1 block">Current password
          <Input type="password" autoComplete="current-password" value={form.old_password} onChange={(e) => setForm({ ...form, old_password: e.target.value })} data-testid="pw-current" aria-label="Current password" />
        </label>
        <label className="text-xs text-muted-foreground space-y-1 block">New password (minimum 8 characters)
          <Input type="password" autoComplete="new-password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="pw-new" aria-label="New password" />
        </label>
        <label className="text-xs text-muted-foreground space-y-1 block">Confirm new password
          <Input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="pw-confirm" aria-label="Confirm new password" />
        </label>
        <Button onClick={change} disabled={busy || !form.old_password || !form.new_password} data-testid="pw-submit">Change password</Button>
      </CardContent>
    </Card>
  );
}

const CATEGORY_LABELS = {
  SHIFTS: 'Shift changes, open shifts, swaps and handovers',
  ALERTS: 'Safety, medication and compliance alerts',
  TASKS: 'Tasks, incidents and document requests',
  SYSTEM: 'Timesheets, leave and system messages',
};

function NotificationsCard() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get('/mobile/notifications/preferences').then((r) => setPrefs(r.data.data)).catch(() => setPrefs(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (cat, enabled) => {
    const muted = enabled ? prefs.mutedCategories.filter((c) => c !== cat) : [...prefs.mutedCategories, cat];
    setBusy(true);
    try {
      const r = await api.put('/mobile/notifications/preferences', { mutedCategories: muted });
      setPrefs({ ...prefs, mutedCategories: r.data.data.mutedCategories });
      toast.success('Notification preferences saved');
    } catch (e) { toast.error(errMsg(e, 'Unable to save preferences')); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="settings-notifications">
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><BellRing className="h-4 w-4" />Notification preferences</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {prefs === null && <div className="text-sm text-muted-foreground">Loading…</div>}
        {prefs === false && <div className="text-sm text-muted-foreground">Notification preferences are unavailable right now.</div>}
        {prefs && (prefs.categories || []).map((cat) => {
          const enabled = !prefs.mutedCategories.includes(cat);
          return (
            <div key={cat} className="flex items-center justify-between gap-3 py-1.5 border-b last:border-0">
              <div>
                <div className="text-sm font-medium">{cat.charAt(0) + cat.slice(1).toLowerCase()}</div>
                <div className="text-xs text-muted-foreground">{CATEGORY_LABELS[cat] || ''}</div>
              </div>
              <Switch checked={enabled} disabled={busy} onCheckedChange={(v) => toggle(cat, v)} data-testid={`notif-toggle-${cat}`} aria-label={`${cat} notifications`} />
            </div>
          );
        })}
        {prefs && prefs.nonMutable?.length > 0 && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-2.5" data-testid="notif-mandatory-note">
            <span className="font-medium">Always on:</span> critical safety, medication and compliance alerts ({prefs.nonMutable.map((n) => n.replace(/_/g, ' ').toLowerCase()).join(', ')}) are mandatory and cannot be turned off.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PrivacyCard({ settings }) {
  return (
    <Card data-testid="settings-privacy">
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Privacy</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <div className="text-sm text-muted-foreground" data-testid="privacy-no-options">There are no optional privacy settings in this release.</div>
        <div className="text-xs text-muted-foreground">The following processing is required and cannot be disabled:</div>
        <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
          {(settings?.privacy?.mandatory_processing || []).map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      </CardContent>
    </Card>
  );
}

function LanguageCard({ settings, reload }) {
  const [busy, setBusy] = useState(false);
  const change = async (code) => {
    setBusy(true);
    try {
      await api.put('/staff/me/settings/language', { language: code });
      toast.success('Language preference saved');
      reload();
    } catch (e) { toast.error(errMsg(e, 'Unable to save language')); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="settings-language">
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Globe2 className="h-4 w-4" />Language</CardTitle></CardHeader>
      <CardContent className="space-y-2 max-w-md">
        <label className="text-xs text-muted-foreground space-y-1 block">Application language
          <select className="w-full border rounded-md h-9 px-2 text-sm" value={settings?.language || 'en'} disabled={busy}
                  onChange={(e) => change(e.target.value)} data-testid="language-select" aria-label="Application language">
            <option value="en">English</option>
          </select>
        </label>
        <div className="text-xs text-muted-foreground" data-testid="language-partial-note">English is currently the only supported language. Additional translations are a future enhancement (localisation status: PARTIAL).</div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const [state, setState] = useState({ settings: null, error: null, loading: true });
  const reload = useCallback(() => {
    api.get('/staff/me/settings').then((r) => setState({ settings: r.data.data, error: null, loading: false }))
      .catch((e) => setState({ settings: null, error: e?.response?.status === 404 ? 'Settings are not enabled for your organisation yet.' : 'Unable to load settings.', loading: false }));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  if (state.loading) return <div className="py-12 text-center text-sm text-muted-foreground" aria-busy="true">Loading…</div>;
  if (state.error) return <div className="py-12 text-center text-sm text-muted-foreground" data-testid="settings-unavailable">{state.error}</div>;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="settings-page">
      <PasswordCard />
      <NotificationsCard />
      <PrivacyCard settings={state.settings} />
      <LanguageCard settings={state.settings} reload={reload} />
      <TwoFactorSettings settings={state.settings} />
    </div>
  );
}
